// LLM Backend Client - Wrapper for calling the llm_backend service
import { LLM_BACKEND_URL, LLM_BACKEND_AUTH_SECRET } from './apiConfig';

export interface ClassifyIntentRequest {
  version: string;
  messageId: string;
  text: string;
  conversationId: string;
  conversationState: string;
  /** Well-defined task state (e.g. subtasks_processing) when in conv_defined_task_classification */
  taskState?: string;
  allowedIntents: string[];
  language: 'en' | 'nl';
  compactContext: Array<{ role: 'user' | 'assistant'; text: string }>;
}

export interface ClassifyIntentResponse {
  messageId: string;
  intentId: string;
  confidence: number;
  reasons?: string[];
  usage: { prompt: number; completion: number };
  version: string;
}

export interface ExtractSlotsRequest {
  version: string;
  messageId: string;
  text: string;
  slotSchema: Record<string, { rules: string; examples: string[] }>;
  language: 'en' | 'nl';
  compactContext?: Array<{ role: 'user' | 'assistant'; text: string }>;
}

export interface ExtractSlotsResponse {
  messageId: string;
  slots: Record<string, any>;
  usage: { prompt: number; completion: number };
  version: string;
}

export interface SplitTasksRequest {
  version: string;
  messageId: string;
  text: string;
  language: 'en' | 'nl';
}

export interface SplitTasksResponse {
  messageId: string;
  tasks: Array<{ description: string; meta?: Record<string, any> }>;
  confidence: number;
  usage: { prompt: number; completion: number };
  version: string;
}

export class LLMBackendClient {
  private baseUrl: string;
  private authSecret: string;

  constructor(baseUrl: string = LLM_BACKEND_URL, authSecret: string = LLM_BACKEND_AUTH_SECRET) {
    this.baseUrl = baseUrl;
    this.authSecret = authSecret;
  }

  private async request<TResponse>(
    endpoint: string,
    body: any
  ): Promise<TResponse> {
    // Debug logging
    console.log('🔵 LLM Backend Request:', {
      url: `${this.baseUrl}${endpoint}`,
      method: 'POST',
      body: JSON.stringify(body, null, 2)
    });
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.authSecret}`
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`LLM Backend error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  async classifyIntent(request: ClassifyIntentRequest): Promise<ClassifyIntentResponse> {
    return this.request<ClassifyIntentResponse>('/v1/classify-intent', request);
  }

  async extractSlots(request: ExtractSlotsRequest): Promise<ExtractSlotsResponse> {
    return this.request<ExtractSlotsResponse>('/v1/extract-slots', request);
  }

  async splitTasks(request: SplitTasksRequest): Promise<SplitTasksResponse> {
    return this.request<SplitTasksResponse>('/v1/split-tasks', request);
  }

  async healthCheck(): Promise<{ status: string; cacheSize: number }> {
    const response = await fetch(`${this.baseUrl}/healthz`);
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status}`);
    }
    return await response.json();
  }
}

export const llmBackendClient = new LLMBackendClient();

